import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { SendRounded } from '@mui/icons-material';
import { platformApi, type TenantInviteRow } from '../../../lib/api/platform.api';
import { formatDateTime, type DateLike } from '../../../lib/i18n-format';

const STATUS_COLORS: Record<TenantInviteRow['status'], 'warning' | 'error' | 'success'> = {
  pending: 'warning',
  expired: 'error',
  used: 'success',
};

export function InvitesTab() {
  const { t } = useTranslation('platform');
  const [invites, setInvites] = useState<TenantInviteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [resending, setResending] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  /** El formato lo resuelve el helper desde i18n; memorizarlo acá volvía a fijar
   *  el idioma a mano. */
  const dateFmt = (v: DateLike) =>
    formatDateTime(v, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { invites: list } = await platformApi.listInvites();
      setInvites(list);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function resend(row: TenantInviteRow) {
    setResending(row.token);
    try {
      await platformApi.resendInvite(row.email, row.tenantId);
      setToast(t('invites.resent', { email: row.email }));
      await load();
    } catch {
      setError(true);
    } finally {
      setResending(null);
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('invites.subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('invites.error')}
        </Alert>
      )}

      {invites.length === 0 ? (
        <Typography color="text.secondary" py={4}>
          {t('invites.empty')}
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('invites.table.email')}</TableCell>
                <TableCell>{t('invites.table.tenant')}</TableCell>
                <TableCell>{t('invites.table.status')}</TableCell>
                <TableCell>{t('invites.table.created')}</TableCell>
                <TableCell>{t('invites.table.expires')}</TableCell>
                <TableCell align="right">{t('invites.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invites.map((row) => (
                <TableRow key={row.token} hover>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.tenantName}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={STATUS_COLORS[row.status]}
                      label={t(`invites.status.${row.status}`)}
                    />
                  </TableCell>
                  <TableCell>{dateFmt(row.createdAt)}</TableCell>
                  <TableCell>{dateFmt(row.expiresAt)}</TableCell>
                  <TableCell align="right">
                    {row.status !== 'used' && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<SendRounded />}
                        disabled={resending === row.token}
                        onClick={() => void resend(row)}
                      >
                        {t('invites.resend')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
